import { useRef, useState, type DragEvent, type ChangeEvent, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface DropzoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  icon?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  hint?: ReactNode;
  className?: string;
}

/**
 * File upload zone. Drag-over detection + click to browse.
 * Touch devices show camera + photo library as alt sources via `action` slot.
 */
export function Dropzone({
  onFiles,
  accept,
  multiple = true,
  icon,
  title,
  subtitle,
  action,
  hint,
  className,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    onFiles(Array.from(files));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOver(true);
  };

  const onDragLeave = () => setIsOver(false);

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      className={cn(
        'mx-auto w-full max-w-2xl p-12 text-center',
        'cursor-pointer rounded-2xl border-2 border-dashed',
        'transition-all duration-fast',
        isOver
          ? 'border-brand-500 bg-brand-50'
          : 'border-border-default bg-bg-surface hover:border-brand-400 hover:bg-brand-50',
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleFiles(e.target.files)}
        className="sr-only"
        aria-label="Choose files"
      />
      {icon && <div className="mb-3 text-4xl">{icon}</div>}
      {title && <div className="mb-2 text-lg font-semibold text-text-primary">{title}</div>}
      {subtitle && <div className="mb-4 text-sm text-text-secondary">{subtitle}</div>}
      {action}
      {hint && <div className="mt-4 font-mono text-[11px] text-text-tertiary">{hint}</div>}
    </div>
  );
}
